import { DatePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, inject, input, OnInit } from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import _ from "lodash";
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    concatMap,
    EMPTY,
    filter,
    finalize,
    forkJoin,
    map,
    Observable,
    of,
    ReplaySubject,
    shareReplay,
    switchMap,
    take,
    tap,
    throwError,
} from "rxjs";
import { AppValidators } from "src/app/common/appValidators";
import { getResourceCategoryIconPath } from "src/app/common/categoryUtils";
import { getPlatformLogoPath } from "src/app/common/platformUtils";
import {
    anyOwaspToDisplayName,
    attackToDisplayName,
    getviolationRuleRiskReasonCardFieldKey,
    getViolationRuleToAttackLinks,
    getViolationRuleToOwaspLinks,
} from "src/app/common/violationRulesUtils";
import { ConfirmationDialogComponent } from "src/app/components/confirmation-dialog/confirmation-dialog.component";
import { LabelsDialogComponent, LabelsDialogInputData, LabelsDialogOutputData } from "src/app/components/labels-dialog/labels-dialog.component";
import { ISecurityTag } from "src/app/components/security-tag/security-tag.component";
import { ISidepanelTab } from "src/app/components/sidepanel-tabs/sidepanel-tabs.component";
import { StatusLabelEnum } from "src/app/components/status-label/status-label.component";
import { ITimelineItem } from "src/app/components/timeline/timeline.component";
import { Labels } from "src/app/labels/englishLabels";
import { IntegrationModel, ResourceType } from "src/app/models/api";
import { LabelBindingModel, LabelModel } from "src/app/models/api/common";
import {
    InventoryApplicationResourceProperties,
    InventoryAutomationResourceProperties,
    InventoryBotResourceProperties,
    InventoryConnectionResourceProperties,
    InventoryConnectorResourceProperties,
    InventoryDataStorageResourceProperties,
    InventoryEntityFieldStatus,
    InventoryEnvironmentResourceProperties,
    InventoryResourceRelationBase,
    ResourceCategory,
} from "src/app/models/api/inventoryResource.model";
import {
    ViolationAudit,
    ViolationModel,
    ViolationNote,
    ViolationsStatusUpdateResponse,
    ViolationStatus,
    ViolationStatusReason,
} from "src/app/models/api/violations.model";
import { PermissionEnum } from "src/app/models/auth0/permission.enum";
import { MenuAction } from "src/app/models/menuAction.model";
import { BeautifyDisplayStringPipe } from "src/app/pipes/beautify-display-string/beautify-display-string.pipe";
import { DisplayCamelCaseStringPipe } from "src/app/pipes/display-camel-case-string/display-camel-case-string.pipe";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { ViolationsActionsService } from "src/app/services/actions/violations/violations-actions.service";
import { ViolationsSupportedActions } from "src/app/services/actions/violations/violationsSupportedActions";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { ViolationsHttpService } from "src/app/services/httpServices/violationsHttpService/violations-http.service";
import { NotificationsService } from "src/app/services/infra/notifications/notifications.service";
import { PermissionsService } from "src/app/services/infra/permissionsService/permissions.service";
import { StoreService } from "src/app/services/infra/store/store.service";
import { InventoryResourceCardFieldBuilder } from "../inventory-resource/inventoryResourceCardFieldBuilder";
import { InventoryHelper } from "../inventory/helpers/inventoryHelper";
import { ViolationRowHelpers } from "../violations/helpers/violationRowHelpers";
import { INoteCardInput, noteCardInputDescending, toAuditCardInput, toNoteCardInput } from "./utils";
import { IViolationsViewModel } from "src/app/models/viewModels/violationsViewModel";
import { JsonHalCollection } from "../../models/api/jsonhal.model";
import { populateErrorMessage } from "../utils/utils";
import { DisplayNameService } from "src/app/services/common/displayName/displayName.service";
import { PolicyMinimalModel } from "src/app/models/api/governance/policy.model";
import { buildResourceTypeDisplayName } from "../../labels/utils";
import { ActivatedRoute } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ICardField } from "src/app/models/viewModels/cardModel";

enum ParentComponentName {
    Violations = "violations",
    Hygiene = "hygiene",
}

@Component({
    selector: "app-violation",
    templateUrl: "./violation.component.html",
    styleUrls: ["./violation.component.scss"],
})
export class ViolationComponent implements OnInit {
    public violation = input.required<string>();
    public integration = input.required<string>();

    public integrationName: string;
    public parentComponentName: ParentComponentName.Violations | ParentComponentName.Hygiene | "" = "";

    public violation$: Observable<ViolationModel | null>;
    public violationNotes$: ReplaySubject<INoteCardInput[]> = new ReplaySubject<INoteCardInput[]>(1);
    public notesNumberTitle$: BehaviorSubject<string> = new BehaviorSubject<string>("0");
    public violationAudits$: BehaviorSubject<ITimelineItem[]> = new BehaviorSubject<ITimelineItem[]>([]);
    public auditsNumberTitle$: BehaviorSubject<string> = new BehaviorSubject<string>("0");
    public noteTextAreaPlaceholder: string = this._labelsProviderPipe.transform("violationPage.noteTextAreaPlaceholder");
    public noteMaxLength = 250;

    // Invalidate note content when it contains dollar sign to prevent MongoDB query selector injection attack,
    // see: https://thecodebarbarian.wordpress.com/2014/09/04/defending-against-query-selector-injection-attacks/
    public noteContentFormControl: UntypedFormControl = new UntypedFormControl("", [Validators.required, AppValidators.noDollarSign]);

    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public notesLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isUpdatingLabels$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public parentIsLoading$ = new BehaviorSubject(false);
    public showNoData = false;
    public menuActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);
    public permissionEnum = PermissionEnum;

    public violationStatusLabel: StatusLabelEnum;
    public violationStatusDescription: string;
    public resourceStatusLabel: StatusLabelEnum;

    public tabs: ISidepanelTab[];
    public activeTabIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    public riskCardFields: ICardField[];
    public summaryCardFields$ = new BehaviorSubject<ICardField[]>([]);
    public resourceCardFields: ICardField[];
    public resourceOwnerCardFieldsList: ICardField[][] = [];
    public findingsCardFieldsList$ = new BehaviorSubject<ICardField[][]>([]);

    public triageSuggestion$ = new BehaviorSubject<ITimelineItem[]>([]);
    public fixSuggestion$ = new BehaviorSubject<ITimelineItem[]>([]);

    public securityRiskTag$ = new BehaviorSubject<ISecurityTag[]>([]);
    public owaspRiskTag$ = new BehaviorSubject<ISecurityTag[]>([]);
    public attackRiskTag$ = new BehaviorSubject<ISecurityTag[]>([]);

    public violationStatusEnum = ViolationStatus;

    public resourceUrl: string;
    public violationExploreLabel: string;
    public iconPath: string;
    public Object = Object;
    public errorMessage$ = new BehaviorSubject<string>("");

    public isActionRunningSpinner = false;

    public graphNotExistLabel: string;
    public linkNotExistTooltip: string;
    // We use the accordion title as a unique identifier.
    // We store the expansion state of the accordion at the parent component
    // in order not to lose it when we switch tabs like we do now.
    public accordionExpansionStateMapping: { [key: string]: boolean };

    public ruleTags$ = new BehaviorSubject<string>("");

    private _notAvailableString: string;

    private readonly _resourcesWithoutPlatformResourceId: string[] = [ResourceType.gateway];

    private _route = inject(ActivatedRoute);
    private _storeService = inject(StoreService);

    constructor(
        public permissionsService: PermissionsService,
        public matDialog: MatDialog,
        private _violationHttpService: ViolationsHttpService,
        private _labelsProviderPipe: LabelsProviderPipe,
        private _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
        private _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
        private _inventoryResourceCardFieldBuilder: InventoryResourceCardFieldBuilder,
        private _violationsActionsService: ViolationsActionsService,
        private _violationsService: ViolationsHttpService,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _notificationService: NotificationsService,
        private _datePipe: DatePipe,
        private _displayNameService: DisplayNameService,
        private _destroyRef: DestroyRef,
    ) {
        this._notAvailableString = this._labelsProviderPipe.transform("common.notAvailable");

        this._initializeAccordionExpansionStateMapping();
    }

    ngOnInit() {
        this.violation$ = this._storeService.getSelectedViolation$();

        this._violationHttpService
            .getViolationById(this.violation(), this.integration(), true)
            .pipe(
                tap((violation) => this._storeService.setSelectedViolation(violation)),
                concatMap(() => this.init()),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();

        this._updateParentComponentNameFromRoute();
    }

    public onTabSelect(index: number) {
        this.activeTabIndex$.next(index);
    }

    public actionSelected(actionString: string) {
        const action = actionString as ViolationsSupportedActions;

        this.violation$
            .pipe(
                take(1),
                filter((violation): violation is ViolationModel => !!violation),
                switchMap((violation: ViolationModel) => this._violationsActionsService.handleActionClicked(action, violation)),
                filter((response): response is boolean | ViolationsStatusUpdateResponse => response !== null && response !== undefined),
                tap((response) => {
                    switch (action) {
                        case ViolationsSupportedActions.PowerplatformSetCopilotAuthentication:
                        case ViolationsSupportedActions.SecureFlowSteps:
                            this.isActionRunningSpinner = true;
                            break;
                        case ViolationsSupportedActions.Exempt:
                        case ViolationsSupportedActions.Waive:
                            this._handleViolationStatusUpdateActionResponse(of(response as ViolationsStatusUpdateResponse));
                            break;
                        default:
                            break;
                    }
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    public noteDeleteOnClick(noteId: string) {
        if (!this.isAddOrDeleteNoteAllowed()) {
            return;
        }

        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            data: {
                title: Labels.violationPage.deleteNoteConfirmationTitle,
                content: Labels.violationPage.deleteNoteConfirmationContent,
            },
            disableClose: false,
            panelClass: "sidepanel-dialog-container",
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((userConfirmed) => !!userConfirmed),
                switchMap(() => {
                    this.notesLoading$.next(true);
                    return this._violationsService.deleteNote(this.violation(), this.integration(), noteId);
                }),
                concatMap(() => this._getViolationNotes()),
                catchError((error) => {
                    this._httpErrorHandlerService.handle(error, error.status === 403 || error.status === 404);
                    return of(error);
                }),
                takeUntilDestroyed(this._destroyRef),
                finalize(() => this.notesLoading$.next(false)),
            )

            .subscribe();
    }

    public addNote() {
        if (!this.isAddOrDeleteNoteAllowed()) return;

        this.notesLoading$.next(true);
        this._violationsService
            .addNote(this.violation(), this.integration(), {
                content: this.noteContentFormControl.value,
            })
            .pipe(
                concatMap(() => this._getViolationNotes()),
                tap(() => this.noteContentFormControl.setValue("")),
                catchError((error) => {
                    this._httpErrorHandlerService.handle(error);
                    return of(error);
                }),
                takeUntilDestroyed(this._destroyRef),
                finalize(() => this.notesLoading$.next(false)),
            )
            .subscribe();
    }

    public isAddOrDeleteNoteAllowed(): boolean {
        return this.permissionsService.hasPermissions(this.permissionEnum.operator);
    }

    public updateAccordionExpansionState(accordionName: string, isExpanded: boolean) {
        this.accordionExpansionStateMapping[accordionName] = isExpanded;
    }

    public removeLabelBindingFromViolation(labelBinding: LabelBindingModel): void {
        this._violationsService
            .unbindLabelFromViolation(this.violation(), this.integration(), labelBinding)
            .pipe(
                concatMap(() => this.violation$.pipe(take(1))),
                filter((violation): violation is ViolationModel => !!violation),
                tap(() => this._notificationService.success("Label removed")),
                map((violation) => {
                    return {
                        ...violation,
                        labelBindings: violation.labelBindings.filter((lb) => lb.id !== labelBinding.id),
                    };
                }),
                concatMap((violation) => {
                    const updatedViolation = structuredClone(violation);
                    this._storeService.setSelectedViolation(updatedViolation);

                    switch (this.parentComponentName) {
                        case ParentComponentName.Violations:
                            return this._storeService.updatePageData(
                                () => this._storeService.getViolationsPageData$(),
                                (data) => this._storeService.setViolationsPageData(data),
                                updatedViolation,
                            );
                        case ParentComponentName.Hygiene:
                            return this._storeService.updatePageData(
                                () => this._storeService.getHygienePageData$(),
                                (data) => this._storeService.setHygienePageData(data),
                                updatedViolation,
                            );
                        default:
                            return of([]);
                    }
                }),
                concatMap(() => this._getViolationAudits()),
                catchError((error) => {
                    this._httpErrorHandlerService.handle(error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    public addLabelsClicked() {
        this.violation$
            .pipe(
                take(1),
                filter((violation): violation is ViolationModel => !!violation),
                switchMap((violation) => {
                    return this.matDialog
                        .open(LabelsDialogComponent, {
                            data: {
                                existingSelectedLabels: violation.labelBindings,
                                titleLabelPath: "labelsDialog.setLabelsTitle",
                            } as LabelsDialogInputData,
                            disableClose: false,
                            autoFocus: false,
                            panelClass: "sidepanel-dialog-container",
                            width: "350px",
                        })
                        .afterClosed()
                        .pipe(map((output: LabelsDialogOutputData | undefined) => ({ output, violation })));
                }),
                filter((response): response is { output: LabelsDialogOutputData; violation: ViolationModel } => !!response.output),
                switchMap(({ output, violation }) => {
                    const labelsToBind = output.labelsToBind.filter(
                        (labelToBind: LabelModel) => !violation.labelBindings.some((label: LabelBindingModel) => label.label.id === labelToBind.id),
                    );
                    const labelsToUnbind = violation.labelBindings.filter(
                        (label: LabelBindingModel) => !output.labelsToBind.some((labelToBind: LabelModel) => labelToBind.id === label.label.id),
                    );
                    this.isUpdatingLabels$.next(true);

                    const bindLabels$: Observable<null | LabelBindingModel[]> =
                        labelsToBind.length > 0
                            ? forkJoin(
                                  labelsToBind.map((label: LabelModel) =>
                                      this._violationHttpService
                                          .bindLabelToViolation(this.violation(), this.integration(), {
                                              labelId: label.id,
                                              description: output.note,
                                          })
                                          .pipe(
                                              catchError((error: HttpErrorResponse) => {
                                                  if (error.status === 409) {
                                                      this._notificationService.error(
                                                          `Could not bind some of the following labels: ${output.labelsToBind.map((l) => l.displayName).join(", ")}.`,
                                                      );
                                                  } else if (error.status === 422 && error.error.errorCode === "LABEL_BINDINGS_QUOTA_REACHED") {
                                                      this._notificationService.error("Cannot bind more than 10 labels to a violation.");
                                                  }

                                                  return throwError(() => error);
                                              }),
                                          ),
                                  ),
                              )
                            : of(null);

                    const unbindLabels$: Observable<null | string[]> =
                        labelsToUnbind && labelsToUnbind?.length > 0
                            ? forkJoin(
                                  labelsToUnbind.map((label: LabelBindingModel) =>
                                      this._violationsService.unbindLabelFromViolation(this.violation(), this.integration(), label).pipe(
                                          catchError((error: HttpErrorResponse) => {
                                              this._notificationService.error(`Could not unbind following label: ${label.label.displayName}.`);
                                              return throwError(() => error);
                                          }),
                                      ),
                                  ),
                              )
                            : of(null);

                    return bindLabels$.pipe(
                        concatMap(() => unbindLabels$),
                        concatMap(() => this._violationHttpService.getViolationById(this.violation(), this.integration(), true)),
                    );
                }),

                concatMap((updatedViolation) => {
                    this._storeService.setSelectedViolation(structuredClone(updatedViolation));
                    this._notificationService.success("Labels updated");

                    switch (this.parentComponentName) {
                        case ParentComponentName.Violations:
                            return this._storeService.updatePageData(
                                () => this._storeService.getViolationsPageData$(),
                                (data) => this._storeService.setViolationsPageData(data),
                                updatedViolation,
                            );
                        case ParentComponentName.Hygiene:
                            return this._storeService.updatePageData(
                                () => this._storeService.getHygienePageData$(),
                                (data) => this._storeService.setHygienePageData(data),
                                updatedViolation,
                            );
                        default:
                            return of([]);
                    }
                }),
                concatMap(() => this._getViolationAudits()),
                catchError((error) => {
                    this._httpErrorHandlerService.handle(error);
                    return of(null);
                }),
                finalize(() => {
                    this.isUpdatingLabels$.next(false);
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    public isGraphExist(): Observable<boolean> {
        return this.violation$.pipe(
            map((violation) => {
                if (violation?.resource._links === null) {
                    this.graphNotExistLabel = Labels.violationPage.graphMessageResourceDeleted;
                    return false;
                }
                // Can add here other cases where the graph is not available
                return true;
            }),
        );
    }

    public checkIfErrorExistsAndPopulateErrorMessage(): Observable<boolean> {
        return this.violation$.pipe(
            map((violation) => {
                if (violation) {
                    const errorMessage = populateErrorMessage(violation.resource);
                    this.errorMessage$.next(errorMessage);

                    if (errorMessage !== "") {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            }),
        );
    }

    public isLinkExist(): Observable<boolean> {
        return this.violation$.pipe(
            map((violation) => {
                if (violation?.resource._links === null) {
                    this.linkNotExistTooltip = Labels.violationPage.summaryCardExploreResourceInventoryLinkDisabled;
                    return false;
                }
                // Can add here other cases where the link is not available
                return true;
            }),
        );
    }

    private init(): Observable<[ViolationModel | null, IntegrationModel[]]> {
        return this._getViolationNotes().pipe(
            tap(() => this.isLoading$.next(true)),
            concatMap(() => this._getViolationAudits()),
            concatMap(() => combineLatest([this.violation$, this._storeService.integrations$])),
            tap(async ([violation, integrations]) => {
                if (violation) {
                    await this._handleDataResponse(violation, integrations, 0);
                }
            }),
            take(1),
            catchError((error) => {
                if (error.status === 404) {
                    this.showNoData = true;
                } else {
                    this._httpErrorHandlerService.handle(error);
                }

                return of(error);
            }),
            takeUntilDestroyed(this._destroyRef),
            finalize(() => this.isLoading$.next(false)),
        );
    }

    private _getViolationNotes(): Observable<ViolationNote[]> {
        return this._violationHttpService.getNotes(this.violation(), this.integration()).pipe(
            tap((notes) => {
                const updatedNotes = structuredClone(notes);
                this.notesNumberTitle$.next(this._getNotesNumberTitle(updatedNotes.length));
                this.violationNotes$.next(updatedNotes.map(toNoteCardInput).sort(noteCardInputDescending));
            }),
            catchError((error) => {
                this._httpErrorHandlerService.handle(error);
                return of(error);
            }),
            shareReplay(1),
            finalize(() => this.isLoading$.next(false)),
        );
    }

    private _getViolationAudits(): Observable<JsonHalCollection<ViolationAudit>> {
        return this._violationHttpService.getAudits(this.violation(), this.integration()).pipe(
            tap((audits: JsonHalCollection<ViolationAudit>) => {
                const auditsCopy = structuredClone(audits);
                this.auditsNumberTitle$.next(this._getAuditsNumberTitle(auditsCopy._embedded.items.length));
                this.violationAudits$.next(auditsCopy._embedded.items.map(toAuditCardInput));
            }),
            catchError((error) => {
                this._httpErrorHandlerService.handle(error);
                return of(error);
            }),
        );
    }

    private async _handleDataResponse(violation: ViolationModel, integrations: IntegrationModel[], activeTabIndex: number): Promise<void> {
        const selectedIntegration: IntegrationModel | undefined = integrations.find((integration) => integration.id === this.integration());
        this.integrationName = selectedIntegration?.name ?? "";

        this.showNoData = false;
        this.iconPath = getPlatformLogoPath(violation.resource.platformInfo.platform);
        this.violationStatusLabel = ViolationRowHelpers.getStatusLabel(violation.status);

        const violationStatusReason = `(Reason: ${this._displayCamelCaseStringPipe.transform(violation.statusReason)}) ${this._shouldShowStatusSince(violation) ? "at " + this._datePipe.transform(violation.statusSince, "MMM d, y, HH:mm") : ""}`;
        this.violationStatusDescription = violationStatusReason;

        this.tabs = [
            { labelPath: "violationPage.violationTabTitle" },
            { labelPath: "violationPage.notesTabTitle" },
            { labelPath: "violationPage.auditsTabTitle" },
        ];
        this._initCardsData();
        const tempView: IViolationsViewModel = {
            id: violation.id,
            name: "",
            labels: [],
            resourceName: "",
            resourceType: "",
            createdBy: "",
            notApplicableCreatedByTooltipText: "",
            ownedBy: [],
            notApplicableOwnedByTooltipText: "",
            platform: violation.resource.platformInfo.platform,
            platformIcon: "",
            owasp: "",
            firstSeen: "",
            resourceId: violation.resource.id,
            ruleId: "",
            integrationId: violation.integration.id,
            violation: violation,
            rowId: "",
            clickable: false,
            ruleTags: [],
        };
        await this._violationsActionsService.setActionsForViolations([violation], [tempView]);

        if (tempView.menuActions) {
            this.menuActions$.next([...tempView.menuActions]);
        }
        this.resourceStatusLabel = InventoryHelper.getViolationRelatedResourceStatusLabel(violation.resource, violation.statusReason);
        this.activeTabIndex$.next(activeTabIndex);
    }

    private _shouldShowStatusSince(violation: ViolationModel): boolean {
        return (
            (violation.status === ViolationStatus.resolved || violation.statusReason == ViolationStatusReason.reopenedByZenity) &&
            violation.statusSince !== undefined
        );
    }

    private _initCardsData(): void {
        this.violation$
            .pipe(
                filter((violation): violation is ViolationModel => !!violation),
                concatMap((violation) => this._initSummaryCardData(violation)),
                concatMap((violation) => this._initFindingsCardData(violation)),
                concatMap((violation) => this._initTriageSuggestionCardData(violation)),
                concatMap((violation) => this._initFixSuggestionCardData(violation)),
                concatMap((violation) => this._initSecurityRisk(violation)),
                concatMap((violation) => this._initAttackRisk(violation)),
                concatMap((violation) => this._initOwaspRisk(violation)),
                concatMap((violation) => this._initRuleTags(violation)),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    private _initSummaryCardData(violation: ViolationModel): Observable<ViolationModel> {
        return this._storeService.accountMinimalPolicies$.pipe(
            map((policies: PolicyMinimalModel[]) => policies.find((item) => item.id === violation.policy.id) ?? null),
            tap((policy) => {
                let summaryCardFields: ICardField[] = [
                    this._inventoryResourceCardFieldBuilder.buildBasicCardFiled("zenityCommon.integration", this.integrationName),
                ];

                if (policy) {
                    summaryCardFields.push(this._inventoryResourceCardFieldBuilder.buildBasicCardFiled("zenityCommon.policy", policy.name));
                }

                summaryCardFields.push(
                    this._inventoryResourceCardFieldBuilder.buildBasicCardFiled(
                        "violationPage.summaryCardFieldResourceName",
                        violation.resource.displayName,
                    ),
                    this._inventoryResourceCardFieldBuilder.buildBasicCardFiled(
                        "violationPage.summaryCardFieldResourceCategoryAndType",
                        buildResourceTypeDisplayName(violation.resource.resourceType),
                        undefined,
                        getResourceCategoryIconPath(violation.resource.resourceCategory),
                    ),
                    this._inventoryResourceCardFieldBuilder.buildBasicCardFiled(
                        "violationPage.summaryCardFieldResourceStatus",
                        this._beautifyDisplayStringPipe.transform(
                            InventoryHelper.getViolationRelatedResourceStatusLabel(violation.resource, violation.statusReason),
                        ),
                    ),
                );

                if (!this._resourcesWithoutPlatformResourceId.includes(violation.resource.resourceType)) {
                    summaryCardFields.push(
                        this._inventoryResourceCardFieldBuilder.buildBasicCardFiled(
                            "violationPage.summaryCardFieldPlatformResourceId",
                            violation.resource.platformInfo.resourceId,
                            undefined,
                            undefined,
                            true,
                        ),
                    );
                }

                const createdByDisplayName = this._displayNameService.getCreatedByDisplayName(
                    violation.resource.identities?.createdBy,
                    violation.resource.resourceAdditionalProperties,
                );
                if (
                    createdByDisplayName !== this._notAvailableString &&
                    ![ResourceCategory.connector, ResourceCategory.identity].includes(violation.resource.resourceCategory)
                ) {
                    // For tenant level resources we should hide the created by field - connector and identity
                    this._pushSummaryCardForInventoryResourceRelationField(
                        summaryCardFields,
                        "violationPage.summaryCardFieldResourceCreatedBy",
                        violation.resource.identities?.createdBy?.value,
                        () => createdByDisplayName,
                    );
                }

                if (
                    this._hasOwnedByRelation(violation) &&
                    ![ResourceCategory.connector, ResourceCategory.identity].includes(violation.resource.resourceCategory)
                ) {
                    summaryCardFields.push(this._getOwnedByField(violation));
                }

                if (violation.resource.environment) {
                    // If we don't have environment we don't want to show the field
                    this._pushSummaryCardForInventoryResourceRelationField(
                        summaryCardFields,
                        "violationPage.summaryCardFieldEnvironment",
                        violation.resource.environment,
                        () => this._beautifyDisplayStringPipe.transform(violation.resource.environment?.displayName),
                    );
                }

                if (violation.resource?.resourceCategoryProperties) {
                    switch (violation.resource.resourceCategory) {
                        case ResourceCategory.automation:
                            this._pushAutomationExtraProperties(
                                violation.resource.resourceCategoryProperties as InventoryAutomationResourceProperties,
                                summaryCardFields,
                            );
                            break;
                        case ResourceCategory.connection:
                            this._pushConnectionExtraProperties(
                                violation.resource.resourceCategoryProperties as InventoryConnectionResourceProperties,
                                summaryCardFields,
                            );
                            break;
                        case ResourceCategory.connector:
                            this._pushConnectorExtraProperties(
                                violation.resource.resourceCategoryProperties as InventoryConnectorResourceProperties,
                                summaryCardFields,
                            );
                            break;
                        case ResourceCategory.environment:
                            this._pushEnvironmentExtraProperties(
                                violation.resource.resourceCategoryProperties as InventoryEnvironmentResourceProperties,
                                summaryCardFields,
                            );
                            break;
                        case ResourceCategory.application:
                            this._pushApplicationExtraProperties(
                                violation.resource.resourceCategoryProperties as InventoryApplicationResourceProperties,
                                summaryCardFields,
                            );
                            break;
                        case ResourceCategory.dataStorage:
                            this._pushDataStorageExtraProperties(
                                violation.resource.resourceCategoryProperties as InventoryDataStorageResourceProperties,
                                summaryCardFields,
                            );
                            break;
                        case ResourceCategory.bot:
                            this._pushBotExtraProperties(
                                violation.resource.resourceCategoryProperties as InventoryBotResourceProperties,
                                summaryCardFields,
                            );
                            break;
                        case ResourceCategory.aiExtension:
                            // Push extra properties for AI extension once they are added
                            break;
                    }
                }

                summaryCardFields.push({
                    key: this._labelsProviderPipe.transform("violationPage.summaryCardFieldRuleId"),
                    value: violation.rule.id,
                    isCopyable: true,
                });

                this.summaryCardFields$.next(structuredClone(summaryCardFields));
            }),
            map(() => violation),
        );
    }

    private _initFindingsCardData(violation: ViolationModel): Observable<ViolationModel> {
        return of(null).pipe(
            tap(() => {
                let result: ICardField[][] = [];

                violation.findings.forEach((dataDict) => {
                    const dataDictCopy = structuredClone(dataDict);
                    const dictSortedKeys = Object.keys(dataDictCopy).sort();

                    dictSortedKeys.forEach((key: string) => {
                        if (_.endsWith(key, "connectorCategory")) {
                            dataDictCopy[key] = this._displayCamelCaseStringPipe.transform(dataDictCopy[key]);
                        }
                    });

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const sortedDict = dictSortedKeys.reduce((r: { [key: string]: any }, k: string) => ((r[k] = dataDictCopy[k]), r), {});

                    const cardFields: ICardField[] = [];
                    for (const key in sortedDict) {
                        const fields: ICardField = {
                            key: this._beautifyDisplayStringPipe.transform(key),
                            value: sortedDict[key],
                            isCopyable: true,
                        };

                        // Quick fix for special cases
                        const lowerKey = key.toLowerCase();
                        if (lowerKey.includes("isssl")) fields.key = "Is SSL";
                        if (lowerKey.includes("flowrunurl")) fields.valueLink = fields.value;

                        cardFields.push(fields);
                    }

                    result.push(cardFields);
                });

                this.findingsCardFieldsList$.next([...result]);
            }),
            map(() => violation),
        );
    }

    private _initTriageSuggestionCardData(violation: ViolationModel): Observable<ViolationModel> {
        return of(null).pipe(
            tap(() => {
                const TriageSuggestionElement = violation.rule.triageSuggestion.split(/\r?\n/);
                const triageSuggestion: ITimelineItem[] = [];

                TriageSuggestionElement.forEach((element) => {
                    triageSuggestion.push({ text: element });
                });

                this.triageSuggestion$.next([...triageSuggestion]);
            }),
            map(() => violation),
        );
    }

    private _initFixSuggestionCardData(violation: ViolationModel): Observable<ViolationModel> {
        return of(null).pipe(
            tap(() => {
                const FixSuggestionElement = violation.rule.fixSuggestion.split(/\r?\n/);
                const fixSuggestion: ITimelineItem[] = [];

                FixSuggestionElement.forEach((element) => {
                    fixSuggestion.push({ text: element });
                });

                this.fixSuggestion$.next([...fixSuggestion]);
            }),
            map(() => violation),
        );
    }

    private _initSecurityRisk(violation: ViolationModel): Observable<ViolationModel> {
        return of(null).pipe(
            tap(() => {
                const securityRiskTag: ISecurityTag[] = [];

                violation.rule.riskLabels.forEach((risk) => {
                    securityRiskTag.push({
                        key: this._labelsProviderPipe.transform("violationPage.riskReasonCardCategoryFieldKey"),
                        value: this._beautifyDisplayStringPipe.transform(risk),
                    });
                });

                this.securityRiskTag$.next([...securityRiskTag]);
            }),
            map(() => violation),
        );
    }

    private _initAttackRisk(violation: ViolationModel): Observable<ViolationModel> {
        return of(null).pipe(
            tap(() => {
                const attackRiskTag: ISecurityTag[] = [];

                violation.rule.attack.forEach((attack) => {
                    attackRiskTag.push({
                        key: this._labelsProviderPipe.transform("violationPage.riskReasonCardAttackFieldKey"),
                        value: this._beautifyDisplayStringPipe.transform(attackToDisplayName[attack]),
                        link: getViolationRuleToAttackLinks(attack),
                    });
                });

                this.attackRiskTag$.next([...attackRiskTag]);
            }),
            map(() => violation),
        );
    }

    private _initOwaspRisk(violation: ViolationModel): Observable<ViolationModel> {
        return of(null).pipe(
            tap(() => {
                const owaspRiskTag: ISecurityTag[] = [];

                violation.rule.owasp.forEach((owasp) => {
                    owaspRiskTag.push({
                        key: this._labelsProviderPipe.transform(getviolationRuleRiskReasonCardFieldKey(owasp)),
                        value: this._beautifyDisplayStringPipe.transform(anyOwaspToDisplayName[owasp]),
                        link: getViolationRuleToOwaspLinks(owasp),
                    });
                });

                this.owaspRiskTag$.next([...owaspRiskTag]);
            }),
            map(() => violation),
        );
    }

    private _initRuleTags(violation: ViolationModel): Observable<null> {
        return of(null).pipe(
            tap(() => {
                if (violation) {
                    this.ruleTags$.next(violation.rule.tags.join(","));
                }
            }),
        );
    }

    private _pushSummaryCardForInventoryResourceRelationField(
        summaryCardFields: ICardField[],
        summaryFieldLabelKey: string,
        relationField: InventoryResourceRelationBase | null | undefined,
        relationNotAvailableFallbackProvider?: () => string,
    ) {
        const resourceRelationFieldKey = this._labelsProviderPipe.transform(summaryFieldLabelKey);

        summaryCardFields.push(
            relationField
                ? {
                      key: resourceRelationFieldKey,
                      value: relationField.displayName,
                      internalNavigation: {
                          page: "",
                          // We set violation to undefined so it's removed from the query string of the current page
                          query: {
                              integration: this.integration(),
                              resource: relationField.id,
                              violation: undefined,
                          },
                      },
                  }
                : {
                      key: resourceRelationFieldKey,
                      value: relationNotAvailableFallbackProvider?.call(this) ?? this._notAvailableString,
                  },
        );
    }

    private _hasOwnedByRelation(violation: ViolationModel): boolean {
        return (
            violation.resource.identities?.ownedBy?.status == InventoryEntityFieldStatus.hasValue &&
            violation.resource.identities?.ownedBy?.value !== null &&
            violation.resource.identities?.ownedBy?.value?.length > 0
        );
    }

    private _pushAutomationExtraProperties(extraProperties: InventoryAutomationResourceProperties, summaryCardFields: ICardField[]) {
        this._inventoryResourceCardFieldBuilder.pushAutomationStatusCardFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushAutomationIsSolutionAwareCardFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushAutomationIsBotTriggerCardFieldIfSuported(extraProperties, summaryCardFields);
    }

    private _pushConnectionExtraProperties(extraProperties: InventoryConnectionResourceProperties, summaryCardFields: ICardField[]) {
        this._inventoryResourceCardFieldBuilder.pushConnectionInUseCardFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushConnectionStatusCardFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushConnectionHostNameCardFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushConnectionAuthenticationTypeCardFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushConnectionConnectorLinkCardField(this.integration(), extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushConnectionsIsOnPremisesCardFieldIfSupported(extraProperties, summaryCardFields);
    }

    private _pushConnectorExtraProperties(extraProperties: InventoryConnectorResourceProperties, summaryCardFields: ICardField[]) {
        this._inventoryResourceCardFieldBuilder.pushConnectorProviderCardFieldsIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushConnectorVersionFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushConnectorBackendServiceUrlFieldIfSupported(extraProperties, summaryCardFields);
    }

    private _pushEnvironmentExtraProperties(extraProperties: InventoryEnvironmentResourceProperties, summaryCardFields: ICardField[]) {
        this._inventoryResourceCardFieldBuilder.pushEnvironmentTypeCardFieldIfSupported(extraProperties, summaryCardFields);
    }

    private _pushApplicationExtraProperties(extraProperties: InventoryApplicationResourceProperties, summaryCardFields: ICardField[]) {
        this._inventoryResourceCardFieldBuilder.pushApplicationQuarantineStatusCardFieldIfSupported(extraProperties, summaryCardFields);
    }

    private _pushDataStorageExtraProperties(extraProperties: InventoryDataStorageResourceProperties, summaryCardFields: ICardField[]) {
        this._inventoryResourceCardFieldBuilder.pushDataStorageTypeCardFieldIfSupported(extraProperties, summaryCardFields);
    }

    private _pushBotExtraProperties(extraProperties: InventoryBotResourceProperties, summaryCardFields: ICardField[]) {
        this._inventoryResourceCardFieldBuilder.pushBotTypeCardFieldIfSupported(extraProperties, summaryCardFields);
        this._inventoryResourceCardFieldBuilder.pushBotAutonomousTriggersCountCardFieldIfSupported(extraProperties, summaryCardFields);
    }

    private _getOwnedByField(violation: ViolationModel): ICardField {
        const hasOwnedByRelation = this._hasOwnedByRelation(violation);
        const ownedByValue = violation?.resource?.identities?.ownedBy?.value;
        const ownedByDisplayName = hasOwnedByRelation && ownedByValue ? ownedByValue[0].displayName : this._notAvailableString;
        const ownedByCardField: ICardField = this._inventoryResourceCardFieldBuilder.buildBasicCardFiled(
            "violationPage.summaryCardFieldResourceOwnedBy",
            ownedByDisplayName,
            "commonEntitiesInfo.ownedBy",
        );

        if (hasOwnedByRelation && ownedByValue) {
            ownedByCardField.internalNavigation = {
                page: "",
                query: {
                    integration: this.integration(),
                    resource: ownedByValue[0].id,
                },
            };
            ownedByCardField.isCopyable = true;

            if (ownedByValue.length > 1) {
                ownedByCardField.additionalValues = ownedByValue.slice(1).map((owner) => owner.displayName);
            }
        }

        return ownedByCardField;
    }

    private _handleViolationStatusUpdateActionResponse(actionResponse: Observable<ViolationsStatusUpdateResponse | undefined>) {
        actionResponse
            .pipe(
                tap((response: ViolationsStatusUpdateResponse | undefined) => {
                    if (response) {
                        const notificationText = "Violation was updated";
                        this._notificationService.success(notificationText);
                    }
                }),
                catchError((error) => {
                    this._notificationService.error(error.error.detail);
                    return of(error);
                }),
                takeUntilDestroyed(this._destroyRef),
                finalize(() => (this.isActionRunningSpinner = false)),
            )
            .subscribe();
    }

    private _getNotesNumberTitle(noteCount: number) {
        return this._getItemsNumberTitle("violationPage.notesTabTitle", noteCount);
    }

    private _getAuditsNumberTitle(auditCount: number) {
        return this._getItemsNumberTitle("violationPage.auditsTabTitle", auditCount);
    }

    private _getItemsNumberTitle(itemsLabel: string, count: number) {
        return `${this._labelsProviderPipe.transform(itemsLabel)} (${count})`;
    }

    private _initializeAccordionExpansionStateMapping() {
        this.accordionExpansionStateMapping = {
            "violationPage.graphCardTitle": true,
            "violationPage.summaryCardTitle": true,
            "violationPage.riskReasonCardTitle": false,
            "violationPage.triageSuggestionTitle": false,
            "violationPage.fixSuggestionTitle": false,
        };
    }

    private _updateParentComponentNameFromRoute(): void {
        const parentUrl = this._route.firstChild?.snapshot.url[0].path;

        if (parentUrl === ParentComponentName.Hygiene) {
            this.parentComponentName = ParentComponentName.Hygiene;
        } else if (parentUrl === ParentComponentName.Violations) {
            this.parentComponentName = ParentComponentName.Violations;
        } else {
            this.parentComponentName = "";
            console.error(`Parent component name value is invalid - ${this.parentComponentName} `);
        }
    }
}
